import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { AVATARS, colorOf } from "@/lib/constants";
import { MEMBER_COLORS, nextFreeAccentKey } from "@/lib/memberColors";
import { formatBirthday } from "@/lib/birthdays";
import { PET_CARE_SPECIES } from "@/lib/vibe";
import { toast } from "sonner";
import { X, Trash2, Plus, Check, ShieldCheck, Cake, ArrowUp, ArrowDown } from "lucide-react";
import FamilySettingsPanel from "@/components/FamilySettingsPanel";
import HousesPanel from "@/components/HousesPanel";
import { useI18n } from "@/lib/i18n";
import AvatarImagePicker from "@/components/AvatarImagePicker";

export default function MembersManager({
  settings,
  members,
  viewerId,
  setViewerId,
  onClose,
  onChanged,
  setMembers,
  setSettings,
  onSignOut,
  onUpgradeNeeded,
  onOpenBilling,
  autoOpenAdd,
  membersOnly = false,
}) {
  const { t } = useI18n();
  const viewer = members.find((m) => m.id === viewerId);
  const isParentViewer = !viewer || viewer.role !== "kid"; // null viewer treated as parent
  const [tab, setTab] = useState("members");
  useEffect(() => {
    // When Settings opens in "members only" mode (houses front), force
    // the tab back to members even if a lingering event or localStorage
    // tried to open Family. Family/device live per-house now.
    if (membersOnly) setTab("members");
  }, [membersOnly]);
  useEffect(() => {
    const onTab = (e) => {
      const t = e?.detail?.tab;
      if (t === "members" || (t === "family" && !membersOnly)) setTab(t);
    };
    window.addEventListener("famfeel:open-manager-tab", onTab);
    return () => window.removeEventListener("famfeel:open-manager-tab", onTab);
  }, [membersOnly]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newMember, setNewMember] = useState({
    name: "",
    avatar_emoji: "🙂",
    avatar_image: "",
    color: "sage",
    accent_color: "sage",
    pin: "",
    role: "parent",
    birthday: "",
    house_id: "",
    pet_species: "dog",
  });
  const [busy, setBusy] = useState(false);
  const [planCap, setPlanCap] = useState(null);
  const [planTier, setPlanTier] = useState("free");
  const [billingSummary, setBillingSummary] = useState(null);
  const [houses, setHouses] = useState([]);
  const [colorCodeBusy, setColorCodeBusy] = useState(false);

  const toggleColorCode = async () => {
    if (colorCodeBusy) return;
    const next = !settings?.color_code_by_member;
    setColorCodeBusy(true);
    try {
      const updated = await api.updateFamilySettings({
        color_code_by_member: next,
      });
      setSettings && setSettings(updated);
      // Nudge the CalendarSheet (and any other listener) to re-read the
      // setting immediately instead of waiting for its 30-second poll.
      window.dispatchEvent(
        new CustomEvent("famfeel:family-settings-updated", {
          detail: { color_code_by_member: next, settings: updated },
        }),
      );
      toast.success(next ? "Color-code on" : "Color-code off");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setColorCodeBusy(false);
    }
  };

  const reloadHouses = async () => {
    try {
      const rows = await api.listHouses();
      setHouses(rows);
    } catch {
      // ignore — non-extended families will 200/empty or 402
      setHouses([]);
    }
  };

  useEffect(() => {
    let alive = true;
    api.billingStatus().then((s) => {
      if (!alive) return;
      setPlanCap(s.max_members);
      setPlanTier(s.tier);
      setBillingSummary(s);
      if (s.tier === "extended" || s.tier === "grand") reloadHouses();
    }).catch(() => {
      // Fallback to free-tier cap so we err on the side of gating, not bypassing.
      if (alive) setPlanCap(4);
    });
    return () => { alive = false; };
  }, []);

  // When the sheet is opened right after a successful upgrade, jump straight
  // into the add-member form — they just paid to add someone.
  useEffect(() => {
    if (autoOpenAdd && planCap !== null && members.length < planCap) {
      setAdding(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenAdd, planCap]);

  // Auto-pick the next unused accent when the Add form opens so siblings
  // never collide on the same shade. The user can still re-pick by
  // tapping any swatch — this just sets a smart default.
  useEffect(() => {
    if (!adding) return;
    const next = nextFreeAccentKey(members);
    setNewMember((prev) => ({ ...prev, color: next, accent_color: next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adding]);

  const startEdit = (m) => {
    setEditing(m.id);
    setDraft({
      name: m.name,
      avatar_emoji: m.avatar_emoji,
      avatar_image: m.avatar_image || "",
      color: m.accent_color || m.color,
      accent_color: m.accent_color || m.color || "",
      pin: "",
      clear_pin: false,
      has_pin: m.has_pin,
      role: m.role || "parent",
      birthday: m.birthday || "",
      house_id: m.house_id || "",
      pet_species: m.pet_species || (m.role === "pet" ? "dog" : ""),
      cross_house_privacy: {
        mood: m.cross_house_privacy?.mood !== false,
        sleep: m.cross_house_privacy?.sleep !== false,
        status: m.cross_house_privacy?.status !== false,
        note: m.cross_house_privacy?.note !== false,
        tags: m.cross_house_privacy?.tags !== false,
      },
    });
  };

  const saveEdit = async (id) => {
    if (draft.role !== "pet" && !draft.birthday) {
      toast.error("Birthday required for people");
      return;
    }
    const target = members.find((x) => x.id === id);
    const currentHouse = target?.house_id || "";
    const nextHouse = draft.house_id || "";
    // First-time placement (unassigned → a house) is free. Only a
    // relocation — moving between two houses — needs the member PIN,
    // since that's when someone else could be changing where you live.
    const houseChanged =
      planTier === "extended" && currentHouse !== nextHouse;
    const isFirstPlacement =
      planTier === "extended" && currentHouse === "" && nextHouse !== "";
    const targetIsSelf = viewerId && viewerId === id;
    const targetRole = draft.role || target?.role || "parent";

    // House-lock front-door PIN — look up cached unlocks for the source
    // (current) and destination (next) houses. `houseLock` has the PIN
    // if unlocked on this device, else null. We send what we have; the
    // backend decides what's required.
    const { getHouseUnlockPin } = await import("@/lib/houseLock");
    const housePinCurrent = currentHouse ? getHouseUnlockPin(currentHouse) : null;
    const housePinNext = houseChanged && nextHouse ? getHouseUnlockPin(nextHouse) : null;

    // Member-level consent for moves (existing rule).
    let memberPin = null;
    if (houseChanged && !isFirstPlacement && !targetIsSelf && targetRole !== "pet") {
      const needsPrompt =
        !!target?.has_pin ||
        targetRole === "parent" ||
        targetRole === "grandparent" ||
        targetRole === "other";
      if (needsPrompt) {
        const name = target?.name || "this member";
        const entered = window.prompt(
          `Moving ${name} to a different house needs their own personal PIN. ` +
            `Ask ${name} to enter it now. This keeps each person in control of which house they live in.`,
          ""
        );
        if (entered === null) {
          toast.info("House move cancelled");
          return;
        }
        memberPin = entered.trim();
      }
    }
    setBusy(true);
    try {
      const payload = {
        name: draft.name,
        avatar_emoji: draft.avatar_emoji,
        avatar_image: draft.avatar_image || "",
        color: draft.color,
        accent_color: draft.accent_color || "",
        role: draft.role,
        birthday: draft.birthday || "",
        pet_species: draft.role === "pet" ? (draft.pet_species || "dog") : "",
      };
      if (planTier === "extended") {
        payload.house_id = draft.house_id || "";
        payload.cross_house_privacy = draft.cross_house_privacy;
      }
      if (draft.clear_pin) payload.pin = "";
      else if (draft.pin.trim()) payload.pin = draft.pin.trim();
      if (housePinCurrent) payload.house_pin = housePinCurrent;
      if (houseChanged) {
        payload.actor_member_id = viewerId || null;
        if (memberPin !== null) payload.member_pin = memberPin;
        if (housePinNext) payload.house_pin_dest = housePinNext;
      }
      await api.updateMember(id, payload);
      toast.success("Saved");
      setEditing(null);
      setDraft(null);
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const del = async (m) => {
    if (!window.confirm(`Remove ${m.name}? Their check-ins will be deleted.`))
      return;
    setBusy(true);
    try {
      const { getHouseUnlockPin } = await import("@/lib/houseLock");
      const pin = m.house_id ? getHouseUnlockPin(m.house_id) : null;
      await api.deleteMember(m.id, pin ? { house_pin: pin } : {});
      setMembers((prev) => prev.filter((x) => x.id !== m.id));
      toast.success(`${m.name} removed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    } finally {
      setBusy(false);
    }
  };

  const move = async (m, direction) => {
    const idx = members.findIndex((x) => x.id === m.id);
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (idx < 0 || target < 0 || target >= members.length) return;
    const next = [...members];
    [next[idx], next[target]] = [next[target], next[idx]];
    setMembers(next); // optimistic
    try {
      await api.reorderMembers(next.map((x) => x.id));
    } catch (e) {
      toast.error("Could not reorder");
      await onChanged();
    }
  };

  const saveNew = async () => {
    if (!newMember.name.trim()) {
      toast.error("Name required");
      return;
    }
    if (newMember.role !== "pet" && !newMember.birthday) {
      toast.error("Birthday required");
      return;
    }
    setBusy(true);
    try {
      await api.createMember({
        name: newMember.name.trim(),
        avatar_emoji: newMember.avatar_emoji,
        avatar_image: newMember.avatar_image || "",
        color: newMember.color,
        accent_color: newMember.accent_color || newMember.color,
        pin: newMember.pin.trim() || null,
        role: newMember.role,
        birthday: newMember.birthday || null,
        ...(newMember.role === "pet"
          ? { pet_species: newMember.pet_species || "dog" }
          : {}),
        ...(planTier === "extended" && newMember.house_id
          ? { house_id: newMember.house_id }
          : {}),
      });
      setAdding(false);
      setNewMember({
        name: "",
        avatar_emoji: "🙂",
        avatar_image: "",
        color: "sage",
        accent_color: "sage",
        pin: "",
        role: "parent",
        birthday: "",
        house_id: "",
        pet_species: "dog",
      });
      await onChanged();
      toast.success("Added");
    } catch (e) {
      const data = e?.response?.data;
      const detail = data && typeof data.detail === "object" ? data.detail : null;
      if (e?.response?.status === 402 && detail?.error === "upgrade_required") {
        // Bubble up to parent so the BillingSheet opens.
        if (onUpgradeNeeded) {
          onUpgradeNeeded({
            title: `Your ${detail.current_tier} plan is full`,
            message: detail.message,
            suggested_tier: detail.suggested_tier,
          });
        } else {
          toast.error(detail.message || "Upgrade required");
        }
      } else {
        toast.error((typeof data?.detail === "string" ? data.detail : null) || "Could not add");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6"
      style={{ background: "rgba(20,20,18,0.45)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="manager-dialog"
    >
      <div
        className="bg-[color:var(--surface)] rounded-xl w-full max-w-xl max-h-[94vh] overflow-y-auto border border-[color:var(--line)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="members-manager"
      >
        <div className="sticky top-0 z-10 bg-[color:var(--surface)] px-5 pt-5 pb-0 border-b border-[color:var(--line-soft)]">
          <div className="flex items-center justify-between pb-4">
            <div>
              <div className="label-tiny">{t({ en: "Settings", da: "Indstillinger" })}</div>
              <h2 className="font-display text-2xl">{t({ en: "Your family", da: "Din familie" })}</h2>
            </div>
            <button
              onClick={onClose}
              className="ff-btn h-9 w-9 !p-0"
              data-testid="manager-close"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setTab("members")}
              className={`px-3 py-2 label-tiny border-b-2 transition-colors ${
                tab === "members"
                  ? "border-[color:var(--ink)] text-[color:var(--ink)]"
                  : "border-transparent hover:text-[color:var(--ink)]"
              }`}
              data-testid="tab-members"
            >
              Members ({members.length}/{planCap ?? "…"})
            </button>
            {isParentViewer && !membersOnly && (
              <button
                onClick={() => setTab("family")}
                className={`px-3 py-2 label-tiny border-b-2 transition-colors ${
                  tab === "family"
                    ? "border-[color:var(--ink)] text-[color:var(--ink)]"
                    : "border-transparent hover:text-[color:var(--ink)]"
                }`}
                data-testid="tab-family"
              >
                Family
              </button>
            )}
          </div>
        </div>

        <div className="p-5 space-y-3">
          {tab === "family" && isParentViewer && settings && (
            <FamilySettingsPanel
              settings={settings}
              onSaved={(s) => setSettings && setSettings(s)}
              onSignOut={onSignOut}
              viewerId={viewerId}
              setViewerId={setViewerId}
              tier={planTier}
              onOpenBilling={onOpenBilling}
              billingSummary={billingSummary}
              onHousesChanged={reloadHouses}
              members={members}
            />
          )}

          {tab === "members" && (
            <>
              {!isParentViewer && (
                <div
                  className="rounded-xl border border-[color:var(--line)] bg-[color:var(--bg-soft)] p-3 flex items-center gap-2 text-sm text-[color:var(--ink-2)]"
                  data-testid="kid-notice"
                >
                  <ShieldCheck className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
                  Only parents can add, edit, or remove members. Ask a parent if
                  something needs to change.
                </div>
              )}
              {members.map((m, idx) => {
                const isEditing = editing === m.id;
                const d = isEditing ? draft : null;
                const isFirst = idx === 0;
                const isLast = idx === members.length - 1;
                return (
                  <div
                    key={m.id}
                    className="rounded-xl border border-[color:var(--line)] p-3"
                    data-testid={`manager-row-${m.id}`}
                  >
                    {!isEditing ? (
                      <div className="flex items-center gap-3">
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center text-2xl flex-shrink-0 overflow-hidden"
                          style={{
                            background: colorOf(m.accent_color || m.color).hex,
                          }}
                        >
                          {m.avatar_image ? (
                            <img src={m.avatar_image} alt={m.name} className="w-full h-full object-cover" />
                          ) : (
                            m.avatar_emoji
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display text-lg leading-tight truncate flex items-center gap-2">
                            {m.name}
                            <span className="label-tiny">
                              {m.role === "pet" ? "pet"
                                : m.role === "kid" ? "kid"
                                : m.role === "grandparent" ? "grandparent"
                                : m.role === "other" ? "other"
                                : "parent"}
                            </span>
                          </div>
                          <div className="label-tiny flex items-center gap-2 flex-wrap">
                            <span>
                              {m.has_pin ? "PIN protected" : "Open profile"}
                            </span>
                            {m.birthday && (
                              <span className="inline-flex items-center gap-1">
                                <Cake className="w-3 h-3" strokeWidth={2} />
                                {formatBirthday(m.birthday)}
                              </span>
                            )}
                            {!m.birthday && m.role !== "pet" && (
                              <span
                                className="inline-flex items-center gap-1 text-[color:var(--rough-strong)]"
                                title="Birthday is required for people — tap edit to add it"
                                data-testid={`birthday-missing-${m.id}`}
                              >
                                <Cake className="w-3 h-3" strokeWidth={2} />
                                Add birthday
                              </span>
                            )}
                          </div>
                        </div>
                        {isParentViewer && (
                          <>
                            <div className="flex flex-col gap-0.5">
                              <button
                                onClick={() => move(m, "up")}
                                disabled={isFirst}
                                className="ff-btn h-7 w-7 !p-0 disabled:opacity-30"
                                data-testid={`move-up-${m.id}`}
                                aria-label={`Move ${m.name} up`}
                              >
                                <ArrowUp className="w-3.5 h-3.5" strokeWidth={2} />
                              </button>
                              <button
                                onClick={() => move(m, "down")}
                                disabled={isLast}
                                className="ff-btn h-7 w-7 !p-0 disabled:opacity-30"
                                data-testid={`move-down-${m.id}`}
                                aria-label={`Move ${m.name} down`}
                              >
                                <ArrowDown className="w-3.5 h-3.5" strokeWidth={2} />
                              </button>
                            </div>
                            <button
                              onClick={() => startEdit(m)}
                              className="ff-btn"
                              data-testid={`edit-${m.id}`}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => del(m)}
                              className="ff-btn h-9 w-9 !p-0 text-[color:var(--rough-strong)]"
                              data-testid={`delete-${m.id}`}
                              aria-label="Delete"
                            >
                              <Trash2 className="w-4 h-4" strokeWidth={2} />
                            </button>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={d.name}
                          onChange={(e) =>
                            setDraft({ ...d, name: e.target.value })
                          }
                          className="ff-input"
                          data-testid={`edit-name-${m.id}`}
                        />
                        <AvatarImagePicker
                          value={d.avatar_image}
                          onChange={(v) => setDraft({ ...d, avatar_image: v })}
                          tier={planTier}
                          onUpgradeClick={() => onOpenBilling && onOpenBilling()}
                        />
                        <div className="flex flex-wrap gap-1">
                          {AVATARS.map((a) => (
                            <button
                              key={a}
                              onClick={() =>
                                setDraft({ ...d, avatar_emoji: a })
                              }
                              className={`w-9 h-9 rounded-full text-lg flex items-center justify-center transition-colors ${
                                d.avatar_emoji === a
                                  ? "bg-[color:var(--ink)] text-[color:var(--bg)]"
                                  : "hover:bg-[color:var(--bg-soft)]"
                              }`}
                            >
                              {a}
                            </button>
                          ))}
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="label-tiny whitespace-nowrap">
                            Or type your own
                          </label>
                          <input
                            type="text"
                            maxLength={8}
                            value={d.avatar_emoji}
                            onChange={(e) =>
                              setDraft({ ...d, avatar_emoji: e.target.value })
                            }
                            onBlur={(e) => {
                              const picked = Array.from(e.target.value.trim())[0] || "🙂";
                              if (picked !== d.avatar_emoji) {
                                setDraft({ ...d, avatar_emoji: picked });
                              }
                            }}
                            placeholder="😺"
                            className="ff-input max-w-[80px] text-center text-xl"
                            data-testid={`edit-custom-emoji-${m.id}`}
                          />
                        </div>
                        <div className="mt-2">
                          <label className="label-tiny block mb-1">Color</label>
                          <div className="flex gap-1.5 flex-wrap">
                            {MEMBER_COLORS.map((c) => (
                              <button
                                key={c.key}
                                type="button"
                                onClick={() => setDraft({ ...d, color: c.key, accent_color: c.key })}
                                className={`w-7 h-7 rounded-full transition-transform border border-[color:var(--line)] ${
                                  (d.accent_color || d.color) === c.key
                                    ? "ring-2 ring-[color:var(--ink)] ring-offset-2"
                                    : ""
                                }`}
                                style={{ background: c.hex }}
                                aria-label={c.label}
                                title={c.label}
                                data-testid={`edit-accent-${c.key}-${m.id}`}
                              />
                            ))}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-3 items-end">
                          <div>
                            <label className="label-tiny block mb-1">Role</label>
                            <div className="flex gap-1 flex-wrap">
                              <button
                                onClick={() => setDraft({ ...d, role: "parent" })}
                                className={`ff-chip ${d.role === "parent" ? "is-active" : ""}`}
                                data-testid={`edit-role-parent-${m.id}`}
                              >
                                Parent
                              </button>
                              <button
                                onClick={() => setDraft({ ...d, role: "kid" })}
                                className={`ff-chip ${d.role === "kid" ? "is-active" : ""}`}
                                data-testid={`edit-role-kid-${m.id}`}
                              >
                                Kid
                              </button>
                              <button
                                onClick={() => setDraft({ ...d, role: "pet" })}
                                className={`ff-chip ${d.role === "pet" ? "is-active" : ""}`}
                                data-testid={`edit-role-pet-${m.id}`}
                              >
                                Pet
                              </button>
                              <button
                                onClick={() => setDraft({ ...d, role: "grandparent" })}
                                className={`ff-chip ${d.role === "grandparent" ? "is-active" : ""}`}
                                data-testid={`edit-role-grandparent-${m.id}`}
                              >
                                Grandparent
                              </button>
                              <button
                                onClick={() => setDraft({ ...d, role: "other" })}
                                className={`ff-chip ${d.role === "other" ? "is-active" : ""}`}
                                data-testid={`edit-role-other-${m.id}`}
                              >
                                Other
                              </button>
                            </div>
                          </div>
                          {d.role === "pet" && (
                            <div className="w-full">
                              <label className="label-tiny block mb-1">What kind of pet?</label>
                              <div className="flex gap-1 flex-wrap" data-testid={`edit-species-row-${m.id}`}>
                                {PET_CARE_SPECIES.map((s) => (
                                  <button
                                    key={s.key}
                                    onClick={() => setDraft({ ...d, pet_species: s.key })}
                                    className={`ff-chip ${d.pet_species === s.key ? "is-active" : ""}`}
                                    data-testid={`edit-species-${s.key}-${m.id}`}
                                    title={
                                      s.key === "dog"
                                        ? "Walk tracker card"
                                        : "Mood + feed + water care card"
                                    }
                                  >
                                    <span className="text-base">{s.emoji}</span> {s.label}
                                  </button>
                                ))}
                              </div>
                              <div className="label-tiny mt-1 text-[color:var(--ink-3)]">
                                {d.pet_species === "dog"
                                  ? "Dogs get the walk tracker. Other species get mood + feeding + water change."
                                  : "This pet's card will show mood, last fed and water change. Everyone in the family can log care."}
                              </div>
                            </div>
                          )}
                          <div className="flex-1 min-w-[160px]">
                            <label className="label-tiny block mb-1">
                              Birthday {d.role === "pet" ? "(optional)" : <span className="text-[color:var(--rough-strong)]">*</span>}
                            </label>
                            <input
                              type="date"
                              value={d.birthday || ""}
                              onChange={(e) =>
                                setDraft({ ...d, birthday: e.target.value })
                              }
                              className="ff-input"
                              required={d.role !== "pet"}
                              data-testid={`edit-birthday-${m.id}`}
                            />
                          </div>
                        </div>
                        {planTier === "extended" && houses.length > 0 && (
                          <div className="pt-2 border-t border-[color:var(--line-soft)]">
                            <label className="label-tiny block mb-1">House</label>
                            <select
                              value={d.house_id || ""}
                              onChange={(e) =>
                                setDraft({ ...d, house_id: e.target.value })
                              }
                              className="ff-input max-w-[240px]"
                              data-testid={`edit-house-${m.id}`}
                            >
                              <option value="">— None (visible to all houses) —</option>
                              {houses.map((h) => (
                                <option key={h.id} value={h.id}>
                                  {h.name}
                                </option>
                              ))}
                            </select>
                            <div className="label-tiny mt-3 mb-1">
                              What the other houses see of {d.name || "this member"}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {[
                                ["mood", "Mood"],
                                ["sleep", "Sleep"],
                                ["status", "Status"],
                                ["note", "Note"],
                                ["tags", "Tags"],
                              ].map(([k, lbl]) => {
                                const on = d.cross_house_privacy?.[k] !== false;
                                // Kids placed in a house can't have mood
                                // or sleep silenced from the other house —
                                // both parents always see how the kid is
                                // doing. Backend enforces the same rule.
                                const lockedOpen =
                                  d.role === "kid" &&
                                  !!d.house_id &&
                                  (k === "mood" || k === "sleep");
                                return (
                                  <button
                                    key={k}
                                    type="button"
                                    disabled={lockedOpen}
                                    onClick={() =>
                                      setDraft({
                                        ...d,
                                        cross_house_privacy: {
                                          ...d.cross_house_privacy,
                                          [k]: !on,
                                        },
                                      })
                                    }
                                    className={`ff-chip ${on ? "is-active" : ""} ${lockedOpen ? "opacity-70 cursor-not-allowed" : ""}`}
                                    data-testid={`edit-privacy-${k}-${m.id}`}
                                    title={
                                      lockedOpen
                                        ? "Always visible — kids' mood and sleep can't be hidden from the other house"
                                        : on
                                          ? "Visible to other houses"
                                          : "Hidden from other houses"
                                    }
                                  >
                                    {on ? "✓" : "✕"} {lbl}{lockedOpen ? " 🔒" : ""}
                                  </button>
                                );
                              })}
                            </div>
                            <div className="label-tiny mt-1 text-[color:var(--ink-3)]">
                              ✓ = visible across houses · ✕ = hidden from other houses. Own house always sees everything.{d.role === "kid" && d.house_id ? " Mood and sleep for kids in a house are always shared with the other house." : ""}
                            </div>
                          </div>
                        )}
                        <div className="pt-2 border-t border-[color:var(--line-soft)]">
                          <label className="label-tiny block mb-2">
                            PIN (4 digits, optional)
                          </label>
                          <div className="flex gap-2 flex-wrap items-center">
                            <input
                              type="password"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              maxLength={4}
                              value={d.pin}
                              onChange={(e) =>
                                setDraft({
                                  ...d,
                                  pin: e.target.value.replace(/\D/g, ""),
                                  clear_pin: false,
                                })
                              }
                              placeholder={d.has_pin ? "●●●●" : "Set a PIN"}
                              className="ff-input max-w-[140px] font-mono tracking-widest"
                              data-testid={`edit-pin-${m.id}`}
                            />
                            {d.has_pin && (
                              <button
                                onClick={() =>
                                  setDraft({
                                    ...d,
                                    clear_pin: !d.clear_pin,
                                    pin: "",
                                  })
                                }
                                className={`ff-chip ${d.clear_pin ? "is-active" : ""}`}
                              >
                                {d.clear_pin ? "Will be removed" : "Remove PIN"}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => {
                              setEditing(null);
                              setDraft(null);
                            }}
                            className="ff-btn"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => saveEdit(m.id)}
                            disabled={busy}
                            className="ff-btn ff-btn-primary"
                            data-testid={`save-${m.id}`}
                          >
                            <Check className="w-4 h-4" strokeWidth={2} /> Save
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {isParentViewer && adding ? (
                <div className="rounded-xl border border-[color:var(--line)] p-3 bg-[color:var(--bg-soft)] space-y-3">
                  <input
                    type="text"
                    value={newMember.name}
                    onChange={(e) =>
                      setNewMember({ ...newMember, name: e.target.value })
                    }
                    placeholder="Name"
                    className="ff-input"
                    data-testid="new-name"
                  />
                  <AvatarImagePicker
                    value={newMember.avatar_image}
                    onChange={(v) => setNewMember({ ...newMember, avatar_image: v })}
                    tier={planTier}
                    onUpgradeClick={() => onOpenBilling && onOpenBilling()}
                  />
                  <div className="flex flex-wrap gap-1">
                    {AVATARS.map((a) => (
                      <button
                        key={a}
                        onClick={() =>
                          setNewMember({ ...newMember, avatar_emoji: a })
                        }
                        className={`w-9 h-9 rounded-full text-lg flex items-center justify-center transition-colors ${
                          newMember.avatar_emoji === a
                            ? "bg-[color:var(--ink)] text-[color:var(--bg)]"
                            : "bg-white hover:bg-[color:var(--surface)]"
                        }`}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="label-tiny whitespace-nowrap">
                      Or type your own
                    </label>
                    <input
                      type="text"
                      maxLength={8}
                      value={newMember.avatar_emoji}
                      onChange={(e) =>
                        setNewMember({ ...newMember, avatar_emoji: e.target.value })
                      }
                      onBlur={(e) => {
                        // On blur, keep only the first grapheme and fall back
                        // to the default smiley if the user cleared it.
                        const picked = Array.from(e.target.value.trim())[0] || "🙂";
                        if (picked !== newMember.avatar_emoji) {
                          setNewMember({ ...newMember, avatar_emoji: picked });
                        }
                      }}
                      placeholder="😺"
                      className="ff-input max-w-[80px] text-center text-xl"
                      data-testid="new-custom-emoji"
                    />
                  </div>
                  <div className="mt-2">
                    <label className="label-tiny block mb-1">Color</label>
                    <div className="flex gap-1.5 flex-wrap">
                      {MEMBER_COLORS.map((c) => (
                        <button
                          key={c.key}
                          type="button"
                          onClick={() => setNewMember({ ...newMember, color: c.key, accent_color: c.key })}
                          className={`w-7 h-7 rounded-full transition-transform border border-[color:var(--line)] ${
                            (newMember.accent_color || newMember.color) === c.key
                              ? "ring-2 ring-[color:var(--ink)] ring-offset-2"
                              : ""
                          }`}
                          style={{ background: c.hex }}
                          aria-label={c.label}
                          title={c.label}
                          data-testid={`new-accent-${c.key}`}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 items-end">
                    <div>
                      <label className="label-tiny block mb-1">Role</label>
                      <div className="flex gap-1 flex-wrap">
                        <button
                          onClick={() => setNewMember({ ...newMember, role: "parent" })}
                          className={`ff-chip ${newMember.role === "parent" ? "is-active" : ""}`}
                          data-testid="new-role-parent"
                        >
                          Parent
                        </button>
                        <button
                          onClick={() => setNewMember({ ...newMember, role: "kid" })}
                          className={`ff-chip ${newMember.role === "kid" ? "is-active" : ""}`}
                          data-testid="new-role-kid"
                        >
                          Kid
                        </button>
                        <button
                          onClick={() => setNewMember({ ...newMember, role: "pet" })}
                          className={`ff-chip ${newMember.role === "pet" ? "is-active" : ""}`}
                          data-testid="new-role-pet"
                        >
                          Pet
                        </button>
                        <button
                          onClick={() => setNewMember({ ...newMember, role: "grandparent" })}
                          className={`ff-chip ${newMember.role === "grandparent" ? "is-active" : ""}`}
                          data-testid="new-role-grandparent"
                        >
                          Grandparent
                        </button>
                        <button
                          onClick={() => setNewMember({ ...newMember, role: "other" })}
                          className={`ff-chip ${newMember.role === "other" ? "is-active" : ""}`}
                          data-testid="new-role-other"
                        >
                          Other
                        </button>
                      </div>
                    </div>
                    {newMember.role === "pet" && (
                      <div className="w-full">
                        <label className="label-tiny block mb-1">What kind of pet?</label>
                        <div className="flex gap-1 flex-wrap" data-testid="new-species-row">
                          {PET_CARE_SPECIES.map((s) => (
                            <button
                              key={s.key}
                              onClick={() => setNewMember({ ...newMember, pet_species: s.key })}
                              className={`ff-chip ${newMember.pet_species === s.key ? "is-active" : ""}`}
                              data-testid={`new-species-${s.key}`}
                            >
                              <span className="text-base">{s.emoji}</span> {s.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex-1 min-w-[160px]">
                      <label className="label-tiny block mb-1">
                        Birthday {newMember.role === "pet" ? "(optional)" : <span className="text-[color:var(--rough-strong)]">*</span>}
                      </label>
                      <input
                        type="date"
                        value={newMember.birthday}
                        onChange={(e) =>
                          setNewMember({ ...newMember, birthday: e.target.value })
                        }
                        className="ff-input"
                        required={newMember.role !== "pet"}
                        data-testid="new-birthday"
                      />
                    </div>
                  </div>
                  {planTier === "extended" && houses.length > 0 && (
                    <div>
                      <label className="label-tiny block mb-1">House (optional)</label>
                      <select
                        value={newMember.house_id || ""}
                        onChange={(e) =>
                          setNewMember({ ...newMember, house_id: e.target.value })
                        }
                        className="ff-input max-w-[240px]"
                        data-testid="new-house"
                      >
                        <option value="">— None (visible to all houses) —</option>
                        {houses.map((h) => (
                          <option key={h.id} value={h.id}>
                            {h.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    value={newMember.pin}
                    onChange={(e) =>
                      setNewMember({
                        ...newMember,
                        pin: e.target.value.replace(/\D/g, ""),
                      })
                    }
                    placeholder="Optional 4-digit PIN"
                    className="ff-input max-w-[220px] font-mono tracking-widest"
                    data-testid="new-pin"
                  />
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => setAdding(false)}
                      className="ff-btn"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveNew}
                      disabled={busy}
                      className="ff-btn ff-btn-primary"
                      data-testid="save-new"
                    >
                      <Check className="w-4 h-4" strokeWidth={2} /> Add
                    </button>
                  </div>
                </div>
              ) : (
                isParentViewer &&
                members.length < 15 && (
                  planCap === null ? (
                    <div
                      className="w-full rounded-xl border border-dashed border-[color:var(--line)] p-4 label-tiny text-center text-[color:var(--ink-3)]"
                      data-testid="manager-add-loading"
                    >
                      Loading your plan…
                    </div>
                  ) : members.length >= planCap ? (
                    <button
                      onClick={() => onUpgradeNeeded && onUpgradeNeeded({
                        title: `Your ${planTier} plan is full`,
                        message: `Allows up to ${planCap} members. Upgrade to add more.`,
                      })}
                      className="w-full rounded-xl border border-dashed border-[color:var(--line)] p-4 font-medium inline-flex items-center justify-center gap-2 hover:bg-[color:var(--bg-soft)] transition-colors"
                      data-testid="manager-upgrade-btn"
                    >
                      <Plus className="w-4 h-4" strokeWidth={2} />
                      Upgrade to add more members
                    </button>
                  ) : (
                    <button
                      onClick={() => setAdding(true)}
                      className="w-full rounded-xl border border-dashed border-[color:var(--line)] p-4 font-medium inline-flex items-center justify-center gap-2 hover:bg-[color:var(--bg-soft)] transition-colors"
                      data-testid="manager-add-btn"
                    >
                      <Plus className="w-4 h-4" strokeWidth={2} />
                      Add family member
                    </button>
                  )
                )
              )}

              {/* Color-code events by member — placed here (just above
                  Houses) because the choice "which colour belongs to
                  whom" lives on this page. Auto-saves on tap so the
                  Members tab stays action-driven (no separate Save). */}
              {isParentViewer && (
                <div className="pt-4 mt-2 border-t border-[color:var(--line-soft)]">
                  <label className="label-tiny block mb-2">Color-code events by member</label>
                  <button
                    type="button"
                    onClick={toggleColorCode}
                    disabled={colorCodeBusy}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 text-[13px] rounded-full border transition-colors ${
                      settings?.color_code_by_member
                        ? "bg-[color:var(--ink)] text-[color:var(--bg)] border-[color:var(--ink)]"
                        : "bg-[color:var(--bg-soft)] text-[color:var(--ink-2)] border-[color:var(--line-soft)] hover:text-[color:var(--ink)]"
                    } ${colorCodeBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                    data-testid="members-color-code-by-member"
                    role="switch"
                    aria-checked={!!settings?.color_code_by_member}
                  >
                    <span
                      className={`w-6 h-3.5 rounded-full relative transition-colors ${
                        settings?.color_code_by_member ? "bg-white/40" : "bg-[color:var(--line)]"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-2.5 h-2.5 rounded-full bg-white transition-all ${
                          settings?.color_code_by_member ? "left-3" : "left-0.5"
                        }`}
                      />
                    </span>
                    {settings?.color_code_by_member ? "On" : "Off"}
                  </button>
                  <p className="mt-1.5 text-[11px] text-[color:var(--ink-3)] max-w-md leading-relaxed">
                    Switch event cards from colored by type of event
                    to personal color codes; each assigned member&apos;s
                    color shows as a stripe on the right side of an
                    event.
                  </p>
                </div>
              )}

              {/* Houses — placed next to members so blended / multi-household
                  families can arrange people into houses without hopping
                  back to Settings. Visible only to parents; gated to
                  Grand and Extended tiers inside the panel itself. */}
              {isParentViewer && (
                <div className="pt-4 mt-2 border-t border-[color:var(--line-soft)]">
                  <HousesPanel
                    tier={planTier}
                    onOpenBilling={onOpenBilling}
                    onChange={reloadHouses}
                    viewerName={viewer?.name || null}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
